/**
 * Form model for landing pages — fields and what happens after a submission.
 *
 * The form is where the page earns its keep, so it is the one block whose shape
 * is fully editable: the operator decides which fields exist, which are
 * required, how wide each one is, and what happens when someone submits.
 *
 * ## Why field names are constrained
 *
 * Each field declares a `nome`. Names in `CAMPOS_CANONICOS` map onto real
 * columns of a company record (`nome`, `email`, `telefone`, `cnpj`, …);
 * anything else is stored as a free answer. Letting the operator invent a field
 * called `email` that lands somewhere other than the e-mail column is how a
 * campaign ends up mailing an empty address, so the canonical names are
 * reserved and their types are fixed.
 *
 * ## Consent is not optional and not editable
 *
 * There is no switch to remove the consent checkbox. A capture form without it
 * produces a record the platform is not allowed to use (LGPD, art. 7º and 8º),
 * which is worse than no record at all — it looks like an asset and is a
 * liability.
 */

export const TIPOS_CAMPO = [
  'texto',
  'email',
  'telefone',
  'cnpj',
  'numero',
  'data',
  'selecao',
  'textarea',
  'checkbox',
] as const

export type TipoCampo = (typeof TIPOS_CAMPO)[number]

export type LarguraCampo = 'inteira' | 'metade'

export interface CampoFormulario {
  id: string
  /** Field name sent to the API. Canonical names map to company columns. */
  nome: string
  tipo: TipoCampo
  rotulo: string
  ajuda?: string
  placeholder?: string
  obrigatorio?: boolean
  largura?: LarguraCampo
  /** For `selecao` only. */
  opcoes?: string[]
}

/**
 * Field names that map onto a company record, with the type each one must have.
 *
 * `email` as a `texto` field would accept "não tenho" and store it as an
 * address; pinning the type here is what keeps the mapping honest.
 */
export const CAMPOS_CANONICOS: Record<string, { tipo: TipoCampo; rotulo: string }> = {
  nome: { tipo: 'texto', rotulo: 'Nome completo' },
  email: { tipo: 'email', rotulo: 'E-mail' },
  telefone: { tipo: 'telefone', rotulo: 'Telefone' },
  cnpj: { tipo: 'cnpj', rotulo: 'CNPJ' },
  razaoSocial: { tipo: 'texto', rotulo: 'Razão social' },
  cargo: { tipo: 'texto', rotulo: 'Cargo' },
  municipio: { tipo: 'texto', rotulo: 'Município' },
  uf: { tipo: 'texto', rotulo: 'UF' },
}

export function ehCampoCanonico(nome: string): boolean {
  return Object.hasOwn(CAMPOS_CANONICOS, nome)
}

// ---------------------------------------------------------------------------
// Ações após o envio
// ---------------------------------------------------------------------------

export type DestinoAposEnvio = 'mensagem' | 'redirecionar'

export interface AcoesFormulario {
  destino: DestinoAposEnvio
  /** Shown in place of the form when `destino === 'mensagem'`. */
  mensagemSucesso: string
  /** Absolute URL. Only used when `destino === 'redirecionar'`. */
  urlRedirecionamento?: string

  /** Segment recorded on the company created by this form. */
  segmento?: string
  /** Publication category the company is interested in. */
  categoriaId?: string
  /** Campaign the new company enters, if any. */
  campanhaId?: string
  /** Internal address notified on each submission. */
  notificarEmail?: string

  /** Label of the button. */
  textoBotao: string
  /** Consent sentence. Mandatory — see the module note. */
  textoConsentimento: string
}

export const ACOES_PADRAO: AcoesFormulario = {
  destino: 'mensagem',
  mensagemSucesso:
    'Recebemos sua solicitação. A equipe da EGBA entra em contato com o orçamento e o prazo.',
  textoBotao: 'Solicitar publicação',
  textoConsentimento:
    'Autorizo o contato sobre a publicação no Diário Oficial e declaro estar ciente da Política de Privacidade.',
}

export const CAMPOS_PADRAO: CampoFormulario[] = [
  { id: 'c-nome', nome: 'nome', tipo: 'texto', rotulo: 'Nome completo', obrigatorio: true, largura: 'inteira' },
  { id: 'c-email', nome: 'email', tipo: 'email', rotulo: 'E-mail', obrigatorio: true, largura: 'metade' },
  { id: 'c-telefone', nome: 'telefone', tipo: 'telefone', rotulo: 'Telefone', largura: 'metade' },
  { id: 'c-razao', nome: 'razaoSocial', tipo: 'texto', rotulo: 'Instituição', largura: 'inteira' },
  { id: 'c-cnpj', nome: 'cnpj', tipo: 'cnpj', rotulo: 'CNPJ', largura: 'metade' },
]

let contadorCampo = 0

export function novoCampo(tipo: TipoCampo = 'texto'): CampoFormulario {
  contadorCampo += 1
  const n = contadorCampo
  return {
    id: `c${Date.now().toString(36)}${n.toString(36)}`,
    nome: `campo_${n}`,
    tipo,
    rotulo: 'Novo campo',
    largura: 'inteira',
    ...(tipo === 'selecao' ? { opcoes: ['Opção 1', 'Opção 2'] } : {}),
  }
}

/**
 * Reads a form block's props in either shape.
 *
 * Pages created before `campos`/`acoes` existed keep the consent text directly
 * on `props.textoConsentimento`, with the fields implied by `pedirCnpj` and
 * `pedirTelefone`. Those pages are in the database and opening one must not
 * crash the editor — which is exactly what happened when `validarFormulario`
 * assumed `acoes` was there.
 *
 * One normaliser, used by the editor, the renderer and the API, so the legacy
 * shape is handled in a single place instead of three that drift.
 */
export function normalizarFormulario(props: Record<string, unknown> | undefined | null): {
  campos: CampoFormulario[]
  acoes: AcoesFormulario
} {
  const p = props ?? {}

  const campos = Array.isArray(p.campos) ? (p.campos as CampoFormulario[]) : formatoAntigo(p)

  const acoesBrutas = (p.acoes ?? {}) as Partial<AcoesFormulario>
  const acoes: AcoesFormulario = {
    ...ACOES_PADRAO,
    ...acoesBrutas,
    // O texto do formato antigo vale como o de agora — a página tinha
    // consentimento, só guardado noutro lugar.
    textoConsentimento:
      acoesBrutas.textoConsentimento ??
      (typeof p.textoConsentimento === 'string' ? p.textoConsentimento : undefined) ??
      ACOES_PADRAO.textoConsentimento,
    textoBotao:
      acoesBrutas.textoBotao ??
      (typeof p.textoBotao === 'string' ? p.textoBotao : undefined) ??
      ACOES_PADRAO.textoBotao,
  }

  return { campos, acoes }
}

/** The fields the old `pedirCnpj`/`pedirTelefone` switches implied. */
function formatoAntigo(p: Record<string, unknown>): CampoFormulario[] {
  return CAMPOS_PADRAO.filter((c) => {
    if (c.nome === 'cnpj') return p.pedirCnpj !== false
    if (c.nome === 'telefone') return p.pedirTelefone !== false
    return true
  })
}

export interface ProblemaCampo {
  campoId: string | null
  mensagem: string
}

/**
 * Validates a form definition.
 *
 * Two rules earn their place: a form with no e-mail field cannot produce a
 * contactable company, and two fields with the same name silently overwrite
 * each other on submission.
 */
export function validarFormulario(
  campos: readonly CampoFormulario[] | undefined | null,
  acoes: Partial<AcoesFormulario> | undefined | null,
): ProblemaCampo[] {
  const problemas: ProblemaCampo[] = []
  const vistos = new Set<string>()

  // Defensivo de propósito: esta função é chamada a cada tecla digitada no
  // editor, sobre dados que vieram do banco. Uma página antiga, um campo pela
  // metade ou um `acoes` ausente não podem derrubar a tela inteira.
  const a: AcoesFormulario = { ...ACOES_PADRAO, ...(acoes ?? {}) }

  for (const c of campos ?? []) {
    const nome = String(c?.nome ?? '').trim()
    if (!nome || !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(nome)) {
      problemas.push({
        campoId: c.id,
        mensagem: 'O nome do campo deve começar por letra e conter só letras, números e _.',
      })
    }
    if (vistos.has(nome)) {
      problemas.push({ campoId: c.id, mensagem: `Já existe um campo chamado "${nome}".` })
    }
    vistos.add(nome)

    if (!String(c?.rotulo ?? '').trim()) {
      problemas.push({ campoId: c.id, mensagem: 'Todo campo precisa de um rótulo visível.' })
    }

    const canonico = CAMPOS_CANONICOS[nome]
    if (canonico && canonico.tipo !== c?.tipo) {
      problemas.push({
        campoId: c.id,
        mensagem: `"${nome}" é um campo do cadastro e precisa ser do tipo ${canonico.tipo}.`,
      })
    }

    if (c?.tipo === 'selecao' && (c.opcoes ?? []).filter((o) => String(o ?? '').trim()).length < 2) {
      problemas.push({ campoId: c.id, mensagem: 'Uma seleção precisa de ao menos duas opções.' })
    }
  }

  if (!vistos.has('email')) {
    problemas.push({
      campoId: null,
      mensagem: 'Sem campo de e-mail o formulário não gera contato aproveitável.',
    })
  }

  if (!a.textoConsentimento.trim()) {
    problemas.push({
      campoId: null,
      mensagem: 'O texto de consentimento é obrigatório (LGPD, art. 8º).',
    })
  }

  if (a.destino === 'redirecionar' && !/^https?:\/\//i.test(a.urlRedirecionamento ?? '')) {
    problemas.push({
      campoId: null,
      mensagem: 'Informe a URL completa (com https://) para onde redirecionar.',
    })
  }

  if (a.destino === 'mensagem' && !a.mensagemSucesso.trim()) {
    problemas.push({
      campoId: null,
      mensagem: 'Escreva a mensagem que aparece depois do envio.',
    })
  }

  return problemas
}
